import { useCallback, useReducer } from 'react';

import {
    TUploadRejectReasonCode,
    TDocuments,
    TFile,
    TFileStatus
} from '../NewDocUploads.types';

enum ACTIONS {
    SET_FILE_SUCCESS = 'SET_FILE_SUCCESS',
    SET_ALL_FILES = 'SET_ALL_FILES',
    SET_FILE_LOADING = 'SET_FILE_LOADING',
    SET_FILE_ERROR = 'SET_FILE_ERROR',
    SET_FILE_REJECT = 'SET_FILE_REJECT',
    REMOVE_FILE = 'REMOVE_FILE'
}

type TSetAllFilesAction = {
    type: typeof ACTIONS.SET_ALL_FILES;
    payload: {
        files: {
            id: string;
            attributes: {
                document_name: string;
                status: string;
                reject_reason?: TUploadRejectReasonCode;
            };
        }[];
    };
};

type TSetLoadingAction = {
    type: ACTIONS.SET_FILE_LOADING;
    payload: {
        id: string;
        fileName: string;
    };
};

type TSetSuccessAction = {
    type: ACTIONS.SET_FILE_SUCCESS;
    payload: {
        id: string;
        status: TFileStatus;
    };
};

type TSetErrorAction = {
    type: ACTIONS.SET_FILE_ERROR;
    payload: {
        id: string;
        errorReason: string;
    };
};

type TRemoveAction = {
    type: ACTIONS.REMOVE_FILE;
    payload: {
        id: string;
    };
};

type TSetRejectAction = {
    type: ACTIONS.SET_FILE_REJECT;
    payload: {
        id: string;
        rejectReason: TUploadRejectReasonCode;
    };
};
type TReducerAction =
    | TSetSuccessAction
    | TSetAllFilesAction
    | TSetLoadingAction
    | TSetErrorAction
    | TSetRejectAction
    | TRemoveAction;

export function useFileUploadReducer() {
    const reducer = (state: TFile[], action: TReducerAction) => {
        switch (action.type) {
            case ACTIONS.SET_ALL_FILES:
                return [
                    ...state,
                    ...action.payload.files.map((file) => {
                        const isRejected =
                            file.attributes.status === 'rejected';

                        const fileData: TFile = {
                            id: file.id,
                            fileName: file.attributes.document_name,
                            status: isRejected
                                ? ('rejected' as TFileStatus)
                                : ('success' as TFileStatus)
                        };

                        if (isRejected) {
                            const rejectReason = file.attributes.reject_reason
                                ? translateUploadRejectReason(
                                      file.attributes.reject_reason
                                  )
                                : 'Document cannot be validated. Please upload another document.';

                            fileData.rejectReason = rejectReason;
                        }

                        return fileData;
                    })
                ];

            case ACTIONS.SET_FILE_LOADING:
                return [
                    ...state,
                    {
                        id: action.payload.id,
                        fileName: action.payload.fileName,
                        status: 'loading' as TFileStatus
                    }
                ];

            case ACTIONS.SET_FILE_SUCCESS: {
                const fileToUpdateIndex = state.findIndex(
                    (file) => file.id === action.payload.id
                );

                const updatedFile = {
                    ...state[fileToUpdateIndex],
                    status: action.payload.status
                };

                return state
                    .slice(0, fileToUpdateIndex)
                    .concat(updatedFile)
                    .concat(state.slice(fileToUpdateIndex + 1));
            }

            case ACTIONS.SET_FILE_ERROR: {
                const fileToUpdateIndex = state.findIndex(
                    (file) => file.id === action.payload.id
                );

                const updatedFile = {
                    ...state[fileToUpdateIndex],
                    status: 'error' as TFileStatus,
                    errorReason: action.payload.errorReason
                };

                return state
                    .slice(0, fileToUpdateIndex)
                    .concat(updatedFile)
                    .concat(state.slice(fileToUpdateIndex + 1));
            }

            case ACTIONS.SET_FILE_REJECT: {
                const fileToUpdateIndex = state.findIndex(
                    (file) => file.id === action.payload.id
                );

                const rejectReason = action.payload.rejectReason
                    ? translateUploadRejectReason(action.payload.rejectReason)
                    : 'Document cannot be validated. Please upload another document.';

                const updatedFile = {
                    ...state[fileToUpdateIndex],
                    status: 'rejected' as TFileStatus,
                    rejectReason: rejectReason
                };

                return state
                    .slice(0, fileToUpdateIndex)
                    .concat(updatedFile)
                    .concat(state.slice(fileToUpdateIndex + 1));
            }

            case ACTIONS.REMOVE_FILE:
                return state.filter((file) => file.id !== action.payload.id);

            default:
                return state;
        }
    };

    const [uploadedFiles, dispatch] = useReducer(reducer, []);

    const setAllFiles = useCallback((files: TDocuments[]) => {
        dispatch({
            type: ACTIONS.SET_ALL_FILES,
            payload: {
                files
            }
        });
    }, []);

    const setFileLoading = useCallback((id: string, fileName: string) => {
        dispatch({
            type: ACTIONS.SET_FILE_LOADING,
            payload: {
                id,
                fileName
            }
        });
    }, []);

    const setFileError = useCallback((id: string, errorReason: string) => {
        dispatch({
            type: ACTIONS.SET_FILE_ERROR,
            payload: {
                id,
                errorReason
            }
        });
    }, []);

    const setFileSuccess = useCallback((id: string) => {
        dispatch({
            type: ACTIONS.SET_FILE_SUCCESS,
            payload: {
                id,
                status: 'success'
            }
        });
    }, []);

    const setFileReject = useCallback(
        (id: string, rejectReason: TUploadRejectReasonCode) => {
            dispatch({
                type: ACTIONS.SET_FILE_REJECT,
                payload: {
                    id,
                    rejectReason
                }
            });
        },
        []
    );

    const removeFile = useCallback((id: string) => {
        dispatch({
            type: ACTIONS.REMOVE_FILE,
            payload: {
                id
            }
        });
    }, []);

    return {
        uploadedFiles,
        setAllFiles,
        setFileLoading,
        setFileSuccess,
        setFileError,
        setFileReject,
        removeFile
    };
}

const translateUploadRejectReason = (reasonCode: TUploadRejectReasonCode) => {
    switch (reasonCode) {
        case 'ILLEGIBLE':
            return 'Documents are illegible';
        case 'PASSWORD_PROTECTED':
            return 'Documents are password protected';
        case 'MISSING_TRANSACTIONS':
            return 'Documents are missing transactions';
        case 'MISSING_ACCOUNT_DETAILS':
            return 'Documents are missing account details';
        case 'MISSING_BALANCES':
            return 'Documents are missing balances';
        case 'INCOMPLETE_REFERENCES':
            return 'Documents are missing references';
        case 'OTHER':
            return 'Unknown issue, please upload new documents';
        case 'DUPLICATE':
            return 'Documents are duplicates';
        case 'UNSUPPORTED_EXTENSION':
            return 'Documents have an invalid extension';
        case 'UNSUPPORTED_FORMAT':
            return 'Documents have an invalid format';
        case 'NO_RELEVANT_DATA':
            return 'Documents do not contain relevant data';
        case 'OUTDATED_INFORMATION':
            return 'Documents contain outdated information';
    }
};
